import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import {
  Stack,
  Text,
  Group,
  Paper,
  Badge,
  Button,
  Flex,
  Tooltip,
} from "@mantine/core"
import { IconGripVertical } from "@tabler/icons-react"
import { useState, useEffect, useMemo } from "react"

import { LabelsExplorerResponses } from "@costory/types/endpoints/labelsExplorer"
import type { AxesFeatureLabel } from "@costory/types/endpoints/labelsExplorer"

function SortableItem({
  id,
  label,
}: {
  id: string
  label: LabelsExplorerResponses.LabelsStatsFullBilling
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <Paper ref={setNodeRef} style={style} withBorder p="xs" mb="xs">
      <Group gap="xs" wrap="nowrap">
        <div
          {...attributes}
          {...listeners}
          style={{ cursor: "grab", flexShrink: 0 }}
        >
          <IconGripVertical size={18} />
        </div>
        <Flex style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
          <Tooltip label={label.key} withArrow>
            <Text size="sm" truncate>
              {label.key}
            </Text>
          </Tooltip>
        </Flex>
        <Badge variant="light" color="gray" size="xs" style={{ flexShrink: 0 }}>
          {((label.proportion_key_present ?? 0) * 100).toFixed(1)}%
        </Badge>
      </Group>
    </Paper>
  )
}

interface MergeLabelsStepProps {
  labels: LabelsExplorerResponses.LabelsStatsFullBilling[]
  onConfirm: (data: AxesFeatureLabel) => void
  completed?: AxesFeatureLabel
}

export function MergeLabelsStep({
  labels,
  onConfirm,
  completed,
}: MergeLabelsStepProps) {
  const initialSources = useMemo(() => {
    if (!Array.isArray(labels)) return {}

    // If we have completed data, use that to initialize the order
    if (completed?.columnsMerged) {
      const completedOrder = completed.columnsMerged.map((col) => col.name)
      const sourcesMap = labels.reduce(
        (acc, curr) => {
          acc[curr.key] = curr
          return acc
        },
        {} as Record<string, LabelsExplorerResponses.LabelsStatsFullBilling>,
      )

      // Reorder based on completed data
      const orderedSources: Record<
        string,
        LabelsExplorerResponses.LabelsStatsFullBilling
      > = {}
      completedOrder.forEach((key) => {
        if (sourcesMap[key]) {
          orderedSources[key] = sourcesMap[key]
        }
      })

      // Add any remaining labels that weren't in the completed data
      labels.forEach((label) => {
        if (!completedOrder.includes(label.key)) {
          orderedSources[label.key] = label
        }
      })

      return orderedSources
    }

    // Default initialization
    return labels.reduce(
      (acc, curr) => {
        acc[curr.key] = curr
        return acc
      },
      {} as Record<string, LabelsExplorerResponses.LabelsStatsFullBilling>,
    )
  }, [labels, completed])

  const [sources, setSources] = useState(initialSources)

  useEffect(() => {
    setSources(initialSources)
  }, [initialSources])

  const sensors = useSensors(useSensor(PointerSensor))

  const orderedKeys = useMemo(() => Object.keys(sources), [sources])

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const newKeys = arrayMove(
      orderedKeys,
      orderedKeys.indexOf(String(active.id)),
      orderedKeys.indexOf(String(over.id)),
    )

    const newSources: Record<
      string,
      LabelsExplorerResponses.LabelsStatsFullBilling
    > = {}
    newKeys.forEach((key) => {
      newSources[key] = sources[key]
    })

    setSources(newSources)
  }

  const handleContinue = () => {
    const axesFeatureLabel: AxesFeatureLabel = {
      name: "",
      columnsMerged: Object.values(sources).map((source) => ({
        name: source.key,
        type: "label" as const,
        ingested: false,
      })),
      maps: [],
    }
    onConfirm(axesFeatureLabel)
  }

  if (labels.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No labels selected for merging
      </Text>
    )
  }

  return (
    <Stack gap="md">
      <Text size="sm">
        Drag and drop to reorder the labels. The order will determine how they
        are merged.
      </Text>

      <Paper
        withBorder
        radius="sm"
        bd="border"
        style={{
          maxHeight: "calc(50vh - 230px)",
          minHeight: "120px",
          overflowY: "auto",
          width: "100%",
          minWidth: 0,
        }}
        p="xs"
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={orderedKeys}
            strategy={verticalListSortingStrategy}
          >
            {orderedKeys.map((key) => (
              <SortableItem key={key} id={key} label={sources[key]} />
            ))}
          </SortableContext>
        </DndContext>
      </Paper>

      <Button
        onClick={handleContinue}
        variant="filled"
        color="primary"
        size="sm"
        fullWidth
      >
        Continue
      </Button>
    </Stack>
  )
}
